import {
  UsersIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PauseCircleIcon,
  BanIcon,
  Building2Icon,
  CalendarIcon,
  AlertCircleIcon,
  Loader2Icon,
  FilterIcon,
  StoreIcon,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'

import { associationsApiCustomer, type Association, associationsApi } from '@nebula/shared/services'

type TabType = 'all' | 'pending' | 'active' | 'rejected' | 'suspended' | 'cancelled'

const MyAssociationsPage = () => {
  // State
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [associations, setAssociations] = useState<Association[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const itemsPerPage = 10

  // Load data on mount and when tab changes
  useEffect(() => {
    loadAssociations()
  }, [activeTab, currentPage])

  // ========================================
  // Data Loading Functions
  // ========================================

  const loadAssociations = async () => {
    try {
      setLoading(true)

      const status = activeTab === 'all' ? undefined : activeTab
      const response = await associationsApiCustomer.listMyAssociations({
        status,
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      })

      setAssociations(response.associations)
      setTotalItems(response.total)
    } catch (error) {
      console.error('Error loading associations:', error)
      toast.error('Erro ao carregar suas associações. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  // ========================================
  // Action Handlers (Memoizados para performance)
  // ========================================

  const handleCancel = useCallback((association: Association) => {
    setSelectedAssociation(association)
    setShowCancelModal(true)
  }, [])

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage)
  }, [])

  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab)
    setCurrentPage(1) // Reset page when changing tabs
  }, [])

  // ========================================
  // Tabs Configuration
  // ========================================

  const tabs: { key: TabType; label: string; icon: any }[] = [
    { key: 'all', label: 'Todas', icon: FilterIcon },
    { key: 'pending', label: 'Pendentes', icon: ClockIcon },
    { key: 'active', label: 'Ativas', icon: CheckCircleIcon },
    { key: 'rejected', label: 'Rejeitadas', icon: XCircleIcon },
    { key: 'suspended', label: 'Suspensas', icon: PauseCircleIcon },
    { key: 'cancelled', label: 'Canceladas', icon: BanIcon },
  ]

  // ========================================
  // Render
  // ========================================

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <UsersIcon className="size-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Minhas Associações</h1>
        </div>
        <p className="text-gray-600">Gerencie suas associações com resellers</p>
      </div>

      {/* Info Box */}
      <div
        className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4"
        role="note"
        aria-label="Informações sobre associações"
      >
        <div className="flex items-start gap-3">
          <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-blue-600" aria-hidden="true" />
          <div>
            <p className="mb-1 text-sm font-medium text-blue-800">Sobre suas Associações</p>
            <p className="text-sm text-blue-700">
              Aqui você pode visualizar todas as suas solicitações de associação com resellers.
              Associações <strong>ativas</strong> permitem que você compre serviços do reseller.
              Você pode cancelar associações ativas ou pendentes a qualquer momento.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b border-gray-200">
          <nav
            className="-mb-px flex overflow-x-auto"
            role="tablist"
            aria-label="Filtrar associações por status"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.key

              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`tabpanel-${tab.key}`}
                  aria-label={`Filtrar por ${tab.label}`}
                  className={`relative flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors focus:ring-2 focus:ring-purple-500 focus:outline-none focus:ring-inset ${
                    isActive
                      ? 'border-b-2 border-purple-600 bg-purple-50 text-purple-600'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  } `}
                >
                  <Icon className="size-4" aria-hidden="true" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
          <Loader2Icon className="size-8 animate-spin text-purple-600" aria-hidden="true" />
          <span className="sr-only">Carregando associações...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && associations.length === 0 && (
        <div className="rounded-lg bg-white p-12 text-center shadow">
          <StoreIcon className="mx-auto mb-4 size-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhuma associação encontrada</h3>
          <p className="mb-4 text-gray-500">
            {activeTab === 'all'
              ? 'Você ainda não possui associações com resellers.'
              : `Você não possui associações com status "${tabs.find((t) => t.key === activeTab)?.label}".`}
          </p>
          {activeTab === 'all' && (
            <a
              href="/resellers"
              aria-label="Ir para marketplace de resellers"
              className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:outline-none"
            >
              <StoreIcon className="size-4" aria-hidden="true" />
              Explorar Marketplace
            </a>
          )}
        </div>
      )}

      {/* Associations List */}
      {!loading && associations.length > 0 && (
        <div className="space-y-4" role="list" aria-label="Lista de associações">
          {associations.map((association) => (
            <AssociationCard
              key={association.id}
              association={association}
              onCancel={() => handleCancel(association)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalItems > itemsPerPage && (
        <div className="mt-6 flex items-center justify-between rounded-lg bg-white px-4 py-3 shadow">
          <div className="text-sm text-gray-700">
            Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>{' '}
            até{' '}
            <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span>{' '}
            de <span className="font-medium">{totalItems}</span> associações
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Página anterior"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              Anterior
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage * itemsPerPage >= totalItems}
              aria-label="Próxima página"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedAssociation && (
        <CancelAssociationModal
          association={selectedAssociation}
          onClose={() => {
            setShowCancelModal(false)
            setSelectedAssociation(null)
          }}
          onSuccess={() => {
            loadAssociations()
          }}
        />
      )}
    </div>
  )
}

// ========================================
// Association Card Component
// ========================================

type AssociationCardProps = {
  association: Association
  onCancel: () => void
}

const AssociationCard = ({ association, onCancel }: AssociationCardProps) => {
  const statusColor = associationsApi.getStatusColor(association.status)
  const statusLabel = associationsApi.getStatusLabel(association.status)
  const statusIcon = associationsApi.getStatusIcon(association.status)
  const canCancel = associationsApiCustomer.canCancel(association)

  return (
    <div className="rounded-lg bg-white p-6 shadow transition-shadow hover:shadow-md">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-purple-100">
            <Building2Icon className="size-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {association.reseller?.name || 'Reseller'}
            </h3>
            <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
              <span>ID: {association.reseller_id.slice(0, 8)}...</span>
            </div>
          </div>
        </div>

        <div className={`rounded-full border px-3 py-1 text-sm font-medium ${statusColor}`}>
          <span className="mr-1">{statusIcon}</span>
          {statusLabel}
        </div>
      </div>

      {/* Timestamps */}
      <div className="mb-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
        <div className="flex items-center gap-2 text-gray-600">
          <CalendarIcon className="size-4" />
          <span>Solicitado em: {associationsApi.formatDate(association.requested_at)}</span>
        </div>
        {association.approved_at && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircleIcon className="size-4" />
            <span>Aprovado em: {associationsApi.formatDate(association.approved_at)}</span>
          </div>
        )}
        {association.rejected_at && (
          <div className="flex items-center gap-2 text-red-600">
            <XCircleIcon className="size-4" />
            <span>Rejeitado em: {associationsApi.formatDate(association.rejected_at)}</span>
          </div>
        )}
        {association.suspended_at && (
          <div className="flex items-center gap-2 text-orange-600">
            <PauseCircleIcon className="size-4" />
            <span>Suspenso em: {associationsApi.formatDate(association.suspended_at)}</span>
          </div>
        )}
        {association.cancelled_at && (
          <div className="flex items-center gap-2 text-gray-600">
            <BanIcon className="size-4" />
            <span>Cancelado em: {associationsApi.formatDate(association.cancelled_at)}</span>
          </div>
        )}
      </div>

      {/* Reasons */}
      {association.rejection_reason && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
          <p className="mb-1 text-sm font-medium text-red-800">Motivo da Rejeição:</p>
          <p className="text-sm text-red-700">{association.rejection_reason}</p>
        </div>
      )}
      {association.suspension_reason && (
        <div className="mb-4 rounded-md border border-orange-200 bg-orange-50 p-3">
          <p className="mb-1 text-sm font-medium text-orange-800">Motivo da Suspensão:</p>
          <p className="text-sm text-orange-700">{association.suspension_reason}</p>
        </div>
      )}
      {association.cancellation_reason && (
        <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3">
          <p className="mb-1 text-sm font-medium text-gray-800">Motivo do Cancelamento:</p>
          <p className="text-sm text-gray-700">{association.cancellation_reason}</p>
        </div>
      )}

      {/* Actions */}
      {canCancel && (
        <div className="border-t border-gray-200 pt-4">
          <button
            onClick={onCancel}
            aria-label={`Cancelar associação com ${association.reseller?.name || 'reseller'}`}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none"
          >
            <BanIcon className="size-4" aria-hidden="true" />
            Cancelar Associação
          </button>
        </div>
      )}
    </div>
  )
}

// ========================================
// Cancel Association Modal
// ========================================

type CancelAssociationModalProps = {
  association: Association
  onClose: () => void
  onSuccess: () => void
}

const CancelAssociationModal = ({
  association,
  onClose,
  onSuccess,
}: CancelAssociationModalProps) => {
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!reason.trim()) {
      setError('Por favor, informe o motivo do cancelamento')
      return
    }

    try {
      setLoading(true)
      setError(null)

      await associationsApiCustomer.cancelAssociation(association.id, reason)

      toast.success(
        `Associação com ${association.reseller?.name || 'reseller'} cancelada com sucesso!`,
      )
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error cancelling association:', error)
      const errorMessage = error.response?.data?.detail || 'Erro ao cancelar associação'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-modal-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 id="cancel-modal-title" className="text-lg font-semibold text-gray-900">
            Cancelar Associação
          </h3>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 px-6 py-4">
            {/* Reseller Info */}
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="mb-2 flex items-center gap-3">
                <div
                  className="flex size-10 items-center justify-center rounded-full bg-red-100"
                  aria-hidden="true"
                >
                  <Building2Icon className="size-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {association.reseller?.name || 'Reseller'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Status: {associationsApi.getStatusLabel(association.status)}
                  </p>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3" role="alert">
              <div className="flex items-start gap-2">
                <AlertCircleIcon
                  className="mt-0.5 size-5 shrink-0 text-yellow-600"
                  aria-hidden="true"
                />
                <div>
                  <p className="mb-1 text-sm font-medium text-yellow-800">Atenção</p>
                  <p className="text-sm text-yellow-700">
                    Ao cancelar esta associação, você não poderá mais comprar serviços deste
                    reseller até que uma nova associação seja aprovada.
                  </p>
                </div>
              </div>
            </div>

            {/* Reason */}
            <div>
              <label
                htmlFor="cancellation-reason"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Motivo do Cancelamento <span className="text-red-500">*</span>
              </label>
              <textarea
                id="cancellation-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explique o motivo do cancelamento..."
                rows={4}
                required
                aria-required="true"
                aria-invalid={!!error}
                aria-describedby={error ? 'reason-error' : undefined}
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-red-500 focus:outline-none"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div
                id="reason-error"
                className="rounded-lg border border-red-200 bg-red-50 p-3"
                role="alert"
                aria-live="assertive"
              >
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              aria-label="Voltar sem cancelar"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-red-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              Voltar
            </button>
            <button
              type="submit"
              disabled={loading}
              aria-label={
                loading ? 'Cancelando associação' : 'Confirmar cancelamento da associação'
              }
              className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden="true" />
                  Cancelando...
                </>
              ) : (
                <>
                  <BanIcon className="size-4" aria-hidden="true" />
                  Confirmar Cancelamento
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MyAssociationsPage
